import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo/index.tsx';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandT } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip/index.tsx';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel/index.tsx';
import { Position } from '@sorare/core/src/components/CornerBadge/index.tsx';
import { TimeLabel } from '@sorare/core/src/components/labels/TimeLabel/index.tsx';
import { ANY_SPORT_SERIES_MARKET } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { useTickerContext } from '@sorare/core/src/contexts/ticker/index.tsx';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';
import {
  LIFECYCLE,
  useLifecycle,
} from '@sorare/core/src/hooks/useLifecycle.ts';
import { glossary } from '@sorare/core/src/lib/glossary.ts';
import { useGetCardSetConfig } from '@sorare/core/src/set/index.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import type {
  SummerCampPlayQuery,
  SummerCampPlayQueryVariables,
} from './__generated__/index.graphql.ts';
import euArt from './assets/euArt.webp';
import lockedArt from './assets/lockedArt.webp';
import mlsArt from './assets/mlsArt.webp';
import playBackground from './assets/playBackground.webp';
import playBackgroundMobile from './assets/playBackgroundMobile.webp';

// ISO8601DateTime is typed as string but can arrive as a Date (mocked schema)
const parseDate = (date: Date | string) =>
  typeof date === 'string' ? parseISO(date) : date;

const SUMMER_CAMP_PLAY_QUERY = gql`
  query SummerCampPlayQuery {
    market {
      id
      cardPackGroup(slug: "european-packs") {
        id
        slug
        startDate
      }
    }
  }
` as TypedDocumentNode<SummerCampPlayQuery, SummerCampPlayQueryVariables>;

/* Banners are 343px wide as in the Figma; the decorative art (players,
   splashes, arrows, globe) is baked into one transparent sheet per state */
const bannerCard = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--double-unit);
  aspect-ratio: 343 / 184;
`;

const bannerText = css`
  display: flex;
  flex-direction: column;
  gap: var(--half-unit);
  text-align: center;
`;

const whiteBezelProps = {
  background: 'var(--c-white)',
  borderColor: 'var(--c-score-medium)',
  borderWidth: 2,
  boxShadow: '0 0 16px rgba(var(--c-rgb-score-medium), 0.6)',
} as const;

const MlsBanner = ({ to, onClick }: { to: string; onClick: () => void }) => (
  <ButtonBase to={to} onClick={onClick} className="w-full">
    <Bezel {...whiteBezelProps}>
      <div
        className={classNames(
          bannerCard,
          css`
            background: url(${mlsArt}) center / cover no-repeat;
          `
        )}
      >
        <Vertical className="text-center ml-auto max-w-1/2" center>
          <Chip size="small" color="set" brand>
            <FormattedMessage {...glossary.new} />
          </Chip>
          <BrandT color="var(--c-black)">
            <FormattedMessage
              defaultMessage="Welcome to {name}"
              values={{ name: <BrandL>MLS</BrandL> }}
            />
          </BrandT>
        </Vertical>
      </div>
    </Bezel>
  </ButtonBase>
);

const LockedEuropeansBanner = ({
  startDate,
}: {
  startDate: Nullable<Date | string>;
}) => {
  const { now } = useTickerContext();
  const daysLeft = startDate
    ? differenceInCalendarDays(parseDate(startDate), now)
    : null;

  return (
    <div
      className={css`
        display: grid;
        > * {
          grid-area: 1 / 1;
        }
      `}
    >
      <Bezel
        background="var(--c-nd-600-opaque)"
        borderColor="rgba(var(--c-rgb-white), 0.2)"
      >
        <Vertical
          center
          className={css`
            aspect-ratio: 343 / 122;
            background: url(${lockedArt}) center / cover no-repeat;
          `}
        >
          <div className={bannerText}>
            <BrandT color="var(--c-white)">
              <FormattedMessage defaultMessage="Europeans leagues" />
            </BrandT>
            <BrandL color="var(--c-white)">
              <FormattedMessage defaultMessage="Coming soon" />
            </BrandL>
          </div>
        </Vertical>
      </Bezel>
      {daysLeft !== null && daysLeft > 0 && (
        <div className="justify-self-start align-self-start mt-1 -mb-0.5">
          <TimeLabel state="date" type="banner" align={Position.LEFT}>
            <FormattedMessage
              defaultMessage="In {days, plural, one {# day} other {# days}}"
              values={{ days: daysLeft }}
            />
          </TimeLabel>
        </div>
      )}
    </div>
  );
};

const UnlockedEuropeansBanner = ({
  to,
  onClick,
}: {
  to: string;
  onClick: () => void;
}) => (
  <ButtonBase to={to} onClick={onClick} className="w-full">
    <Bezel {...whiteBezelProps}>
      <div
        className={classNames(
          bannerCard,
          css`
            background: url(${euArt}) center / cover no-repeat;
          `
        )}
      >
        <div className={bannerText}>
          <BrandT color="var(--c-black)">
            <FormattedMessage
              defaultMessage="Welcome to {name}"
              values={{
                name: (
                  <BrandL>
                    <FormattedMessage defaultMessage="Europeans leagues" />
                  </BrandL>
                ),
              }}
            />
          </BrandT>
        </div>
      </div>
    </Bezel>
  </ButtonBase>
);

export const SummerCampPlay = () => {
  const { generateSportContextPath } = useSportContext();
  const { now } = useTickerContext();
  const { update } = useLifecycle();

  const { data } = useQuery(SUMMER_CAMP_PLAY_QUERY, { errorPolicy: 'all' });

  const getCardSetConfig = useGetCardSetConfig();
  const { SetLogoImg } = getCardSetConfig('colors').visual.brand;

  const startDate = data?.market?.cardPackGroup?.startDate;
  const europeanLeaguesAvailable = !!startDate && parseDate(startDate) <= now;

  const marketPath = generateSportContextPath(ANY_SPORT_SERIES_MARKET);

  return (
    <Vertical
      center
      className={css`
        min-height: calc(var(--100vh) - var(--current-stack-height, 0px));
        padding: var(--triple-unit) var(--double-unit)
          calc(var(--bottom-bar-height-mobile, 0px) + 8 * var(--unit));
        background:
          linear-gradient(to bottom, var(--c-nl-500), var(--c-nl-500)),
          url(${playBackgroundMobile}) center top / cover no-repeat;
        @media ${laptopAndAbove} {
          background-image:
            linear-gradient(to bottom, var(--c-nl-500), var(--c-nl-500)),
            url(${playBackground});
        }
      `}
    >
      <Vertical gap={1} className="pt-4 pb-3" center>
        <SorareLogo size="small" />
        {!!SetLogoImg && <SetLogoImg width={211} />}
      </Vertical>
      <Vertical
        gap={1.5}
        className={css`
          max-width: 334px;
          width: 100%;
          margin-top: auto;
          @media ${laptopAndAbove} {
            margin-top: unset;
          }
        `}
      >
        <MlsBanner
          to={marketPath}
          onClick={() => {
            update(LIFECYCLE.summerCampOnboarded, true);
            update(LIFECYCLE.setSummerCampOnboarded, true);
          }}
        />
        {europeanLeaguesAvailable ? (
          <UnlockedEuropeansBanner
            onClick={() => {
              update(LIFECYCLE.summerCampOnboarded, true);
              update(LIFECYCLE.setSummerCampOnboarded, true);
            }}
            to={marketPath}
          />
        ) : (
          <LockedEuropeansBanner startDate={startDate} />
        )}
        <Button
          fullWidth
          color="primary"
          size="large"
          to={marketPath}
          onClick={() => {
            update(LIFECYCLE.summerCampOnboarded, true);
            update(LIFECYCLE.setSummerCampOnboarded, true);
          }}
        >
          <FormattedMessage defaultMessage="Find players" />
        </Button>
      </Vertical>
    </Vertical>
  );
};
